// French commune name rules: connecting words stay lowercase, hyphens preserved.
// E.g. "nieul-sur-mer" → "Nieul-sur-Mer", "les-avenieres-veyrins-thuellin" →
// "Les-Avenieres-Veyrins-Thuellin" (diacritics can't be restored from a stripped slug).
const LOWERCASE_WORDS = new Set([
  'sur', 'sous', 'le', 'la', 'les', 'de', 'du', 'des',
  'en', 'aux', 'et', 'au', 'lez', 'l', 'd',
])

export function formatCityName(slug: string | undefined | null): string {
  if (!slug) return ''
  return slug
    .split('-')
    .map((word, i) => {
      if (i > 0 && LOWERCASE_WORDS.has(word)) return word
      if (!word) return word
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join('-')
}

/**
 * Inverse of `formatCityName`: any commune display name or slug → the canonical
 * URL form (lowercase, accent-stripped, spaces/apostrophes → '-'). Idempotent.
 *
 * Lives here, not in geo-city.server.ts, because components build city links
 * during render and must not pull a server-only module into the client bundle.
 * `slugMatch` applies the same transform in SQL — keep the two in step.
 */
export function citySlugify(input: string | undefined | null): string {
  if (!input) return ''
  return input
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

/**
 * The diacritic pairs Postgres TRANSLATE() uses to fold commune names, kept
 * beside the JS slugifier so the two can never disagree.
 *
 * They did disagree: the shipped `to` string carried one 'o' too many, which
 * shifted every mapping from index 19 on — ù→o, ç→n, and the whole uppercase
 * half garbled. "Besançon" folded to "besannon" and matched no row, on every
 * route that inlined its own copy of the expression (rge, cee, problemes,
 * tarifs, og, geocode). One definition now; tests/city-slug.test.ts asserts the
 * SQL fold and citySlugify() agree character for character.
 */
export const FOLD_FROM = 'àáâãäåèéêëìíîïòóôõöùúûüýÿñçÀÁÂÃÄÅÈÉÊËÌÍÎÏÒÓÔÕÖÙÚÛÜÝŸÑÇ'
export const FOLD_TO = 'aaaaaaeeeeiiiiooooouuuuyyncAAAAAAEEEEIIIIOOOOOUUUUYYNC'
